/* eslint-disable react-hooks/exhaustive-deps */
// Hero "Busca por IA" (modelo_busca = 4): input de frase natural → buscaIa (site-graphql)
// → monta a URL REAL da /imoveis (mesmo formato do renderPath dos WelcomePieces:
// /imoveis/<transacao>/<cidade>/<bairro>/<empreendimento>/<tipo>?min=&max=&dormitorios=...)
// e navega. NUNCA trava: erro de rede/IA degrada pra busca normal (cidade por substring
// ou /imoveis geral). O default export é um ErrorBoundary com `fallback` (WelcomePieces
// padrão) pra qualquer erro de render/dados não derrubar a home.
// A lógica (frase → filtros → URL) mora em useBuscaIa, compartilhada com o hero 10
// (Padrão + IA) — aqui fica só a tela.
import React, { useState } from 'react';
import { ensureReadableTextColor, isColorLight } from '../../../utils/FunctionsGlobals';
import { getHeroVideoPoster, useHeroVideoReady } from '../../../utils/heroVideo';
import { genMetaEventId, trackMetaEvent } from '../../../utils/metaPixel';
import DrawerComponent from '../../drawer/DrawerComponent';
import CardsBusca from '../../shared/cards-busca/CardsBusca';
import { SparkIcon, useBuscaIa } from './useBuscaIa';
import styles from './WelcomeIa.module.css';
import { usePreviaBuilder } from '../../../lib/previaBuilder';

// 03/09 (Daniel): a "frase da busca 2" DENTRO do campo vale UNICAMENTE para as licenças
// listadas — pedido do cs2387. Sem este gate a 1ª versão (dc42be3) trocou o exemplo pelo
// H2 em ~19 sites do hero 4 que já tinham a frase preenchida, sem ninguém pedir. Os demais
// tenants seguem com o exemplo gerado. Identidade: `Number(site?.id ?? codsite)` (mesma
// convenção do FeaturedHomeCarousel).
const CODSITES_FRASE_NO_CAMPO = new Set<number>([2387]);
// 19/10: frase FIXA dentro do campo, por licença (pedido do cs2291 — o exemplo gerado saía
// "apartamento 2 quartos em Gramado até R$ 600 mil"). Não usa a FraseBusca2 de propósito: a
// do 2291 é outro texto (subtítulo nos demais heros) e não deve ser sobrescrita no banco.
const PLACEHOLDER_FIXO_POR_CODSITE: Record<number, string> = {
    2291: 'Apartamento 3 quartos em Jurerê até R$ 4 MM',
};

interface IWelcomeIaProps {
    allCities: Array<{ label: string; value: any; id_estado?: any }>;
    allTypes: Array<{ label: string; value: any }>;
    allTransacao: Array<{ label: string; value: string }>;
    site: any;
    urlBanner?: string;
    origin?: any;
    codsite?: any; // codsite p/ headers das queries (mesma convenção dos WelcomePieces)
    codsite2?: any; // codsite da query string (?codsite=) p/ ambientes de teste
    valores?: any; // {precos_venda: [{min,max}], precos_locacao: [{min,max}]}
    // drawer do menu mora dentro do hero (mesmo padrão dos WelcomePieces)
    setActiveDrawer?: any;
    activeDrawer?: any;
    favorito?: Array<number>;
    props?: any; // allDataHome (menus etc.)
    allCards?: any; // atalhos de busca cadastrados no CRM (tbl_card_busca)
    fallback?: React.ReactNode;
}

const WelcomeIaHero: React.FC<IWelcomeIaProps> = ({
    allCities,
    allTypes,
    allTransacao,
    site,
    urlBanner,
    origin,
    codsite,
    codsite2,
    valores,
    setActiveDrawer,
    activeDrawer,
    favorito,
    props,
    allCards,
}) => {
    const { videoParado } = usePreviaBuilder(); // prévia dos cards do CRM: vídeo parado na capa
    const [modoCodigo, setModoCodigo] = useState(false);
    const [codigo, setCodigo] = useState('');

    const {
        texto,
        setTexto,
        loading,
        mensagem,
        desambiguacao,
        limparDesambiguacao,
        transacaoSel,
        escolherTransacao,
        opcoesTransacao,
        placeholder: placeholderExemplo,
        submit,
        escolherDesambiguacao,
        buscarPorCodigo,
    } = useBuscaIa({ allCities, allTypes, allTransacao, site, origin, codsite, codsite2, valores });

    const bannerSrc = urlBanner && urlBanner !== 'undefined' ? urlBanner.replaceAll('"', '') : '';

    // Vídeo de fundo do hero (mesmo site.url_video da busca padrão): pôster leve no LCP,
    // vídeo entra depois via useHeroVideoReady — igual ao WelcomePieces (busca 1).
    const videoSrc = site?.url_video && site.url_video !== 'undefined' ? String(site.url_video) : '';
    const heroPoster = getHeroVideoPoster(videoSrc);
    const heroVideoReady = useHeroVideoReady();

    const buttonBackground = site?.cor2 || '#1e1e1e';
    const buttonPreferredTextColor = isColorLight(site?.cor2) ? site?.cor11 : site?.cor13;
    const buttonTextColor = ensureReadableTextColor(buttonPreferredTextColor, buttonBackground) || '#ffffff';

    const mostrarToggleTransacao = opcoesTransacao.length > 1;

    const headline = site?.frase_busca
        ? String(site.frase_busca).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
        : 'Descreva o imóvel dos seus sonhos';

    // O texto DENTRO do campo é, por padrão, um EXEMPLO do que digitar (esta busca é por
    // frase — o visitante precisa ver o formato). Só que neste hero a "frase da busca 2"
    // (H2 em Textos & SEO) não tem onde aparecer: o texto acima do campo é fixo. Então,
    // aqui, quem escreveu essa frase está falando DESTE lugar, e ela manda sobre o exemplo.
    // Fica só no hero 4 de propósito: nos heros 10/12 o `useBuscaIa` é o mesmo, mas lá o
    // H2 já aparece na tela (WelcomePieces) e a frase sairia duplicada.
    const fraseNoCampoLiberada = CODSITES_FRASE_NO_CAMPO.has(Number(site?.id ?? codsite));
    const fraseDoTenant = fraseNoCampoLiberada && site?.frase_busca_2
        ? String(site.frase_busca_2).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
        : '';
    const placeholder = fraseDoTenant || PLACEHOLDER_FIXO_POR_CODSITE[Number(site?.id ?? codsite)] || placeholderExemplo;

    const submitCodigo = () => buscarPorCodigo(codigo);

    const onSubmitForm = (event: React.FormEvent) => {
        event.preventDefault();
        limparDesambiguacao();
        if (texto.trim()) {
            const eventId = genMetaEventId();
            try { sessionStorage.setItem('meta:searchEventId', JSON.stringify({ id: eventId, at: Date.now() })); } catch { /* sessionStorage indisponível — Search sai sem dedup c/ CAPI */ }
            trackMetaEvent(site, 'Search', {
                search_string: texto,
                transaction_type: transacaoSel,
            }, { eventId });
        }
        submit();
    };

    return (
        <div className={styles.welcomeHero}>
            <div className={styles.heroMedia}>
                {videoSrc ? (
                    <video
                        autoPlay={!videoParado}
                        muted
                        loop
                        playsInline
                        className={styles.heroMediaVideo}
                        poster={heroPoster}
                        src={heroPoster ? (heroVideoReady ? videoSrc : undefined) : videoSrc}
                        preload={heroPoster ? 'none' : videoParado ? 'metadata' : 'auto'}
                    >
                        <track kind="captions" label="Legenda" src="legenda.vtt"></track>
                    </video>
                ) : bannerSrc ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img className={styles.heroMediaImg} src={bannerSrc} alt="Banner" />
                ) : (
                    <div className={styles.heroMediaGradient} aria-hidden="true" />
                )}
            </div>
            <div className={styles.heroOverlay} aria-hidden="true" />
            <DrawerComponent
                site={site}
                activeDrawer={activeDrawer}
                setActiveDrawer={setActiveDrawer}
                favorito={favorito || []}
                menus={props?.menus}
            />
            <div className={styles.body}>
                <div className={styles.inner}>
                    <h1 className={styles.headline}>{headline}</h1>
                    <p className={styles.subHeadline}>
                        Escreva com suas palavras — a nossa busca inteligente encontra os imóveis certos.
                    </p>
                    {mostrarToggleTransacao ? (
                        <div
                            role="group"
                            aria-label="Tipo de negócio"
                            style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 12 }}
                        >
                            {opcoesTransacao.map((opt) => (
                                <button
                                    key={opt.v}
                                    type="button"
                                    onClick={() => escolherTransacao(opt.v)}
                                    aria-pressed={transacaoSel === opt.v}
                                    style={{
                                        padding: '6px 18px',
                                        border: '1px solid rgba(255,255,255,.55)',
                                        borderRadius: 999,
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        background: transacaoSel === opt.v ? buttonBackground : 'rgba(255,255,255,.12)',
                                        color: transacaoSel === opt.v ? buttonTextColor : '#fff',
                                    }}
                                >
                                    {opt.l}
                                </button>
                            ))}
                        </div>
                    ) : null}
                    {/* id="myLink": âncora do botão "BUSCA AVANÇADA" do topo (topo2). */}
                    <form id="myLink" className={styles.searchBox} onSubmit={onSubmitForm}>
                        <label htmlFor="welcome-ia-texto" style={{ display: 'none' }}>
                            Descreva o imóvel que você procura
                        </label>
                        <input
                            id="welcome-ia-texto"
                            className={styles.input}
                            type="text"
                            inputMode="search"
                            autoComplete="off"
                            placeholder={placeholder}
                            value={texto}
                            disabled={loading}
                            onChange={(e) => setTexto(e.target.value)}
                        />
                        <button
                            type="submit"
                            className={styles.button}
                            disabled={loading}
                            style={{ background: buttonBackground, color: buttonTextColor }}
                            aria-label="Buscar imóveis com inteligência artificial"
                        >
                            {loading ? <span className={styles.spinner} aria-hidden="true" /> : <SparkIcon className={styles.sparkIcon} />}
                            {loading ? 'Entendendo sua busca...' : 'Buscar'}
                        </button>
                    </form>
                    {modoCodigo ? (
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                // Código de imóvel é ALFANUMÉRICO (ex.: TE1035) — inputMode
                                // numeric abria o teclado só de números no celular e o
                                // cliente não conseguia digitar as letras (cs2504).
                                inputMode="search"
                                autoComplete="off"
                                className={styles.input}
                                placeholder="Digite o código do imóvel"
                                value={codigo}
                                disabled={loading}
                                onChange={(e) => setCodigo(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        submitCodigo();
                                    }
                                }}
                                style={{ maxWidth: 280 }}
                            />
                            <button
                                type="button"
                                className={styles.button}
                                onClick={submitCodigo}
                                disabled={loading || !codigo.trim()}
                                style={{ background: buttonBackground, color: buttonTextColor }}
                            >
                                Buscar código
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setModoCodigo(true)}
                            style={{
                                marginTop: 10,
                                background: 'transparent',
                                border: 'none',
                                color: '#fff',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: 14,
                                opacity: 0.92,
                            }}
                        >
                            🔎 Já sabe o código? Buscar por código do imóvel
                        </button>
                    )}
                    <div className={styles.statusLine} role="status" aria-live="polite">
                        {mensagem}
                    </div>
                    {desambiguacao ? (
                        <ul className={styles.chips}>
                            {desambiguacao.opcoes.map((op, idx) => (
                                <li key={idx}>
                                    <button
                                        type="button"
                                        className={styles.chip}
                                        disabled={loading}
                                        onClick={() => escolherDesambiguacao(op)}
                                        title={op.label}
                                    >
                                        {op.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : null}

                    {/* Atalhos do cliente. Levam rótulo porque os chips acima são sugestões
                        de frase para a IA — mesma forma, função diferente. */}
                    <CardsBusca
                        cards={allCards}
                        site={site}
                        variante="claro"
                        titulo="Busca rápida"
                        className={styles.cards}
                    />
                </div>
            </div>
        </div>
    );
};

// ErrorBoundary: se o hero IA quebrar por QUALQUER motivo (dados/render), cai no
// fallback (WelcomePieces padrão) passado pela home — a home nunca fica sem hero.
class WelcomeIa extends React.Component<IWelcomeIaProps, { hasError: boolean }> {
    constructor(props: IWelcomeIaProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch() {
        // silencioso de propósito: hero degrada pro padrão sem quebrar a home
    }

    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError) return fallback ?? null;
        if (!site) return fallback ?? null;
        return <WelcomeIaHero {...this.props} />;
    }
}

export default WelcomeIa;
